import React, { ForwardedRef, useEffect, useMemo } from 'react';
import CustomShaderMaterial from '../CustomShaderMaterial';
import { BackSide, Color, FrontSide, NormalBlending, ShaderMaterial, Vector3 } from 'three';
import { useThree } from '@react-three/fiber';
import { Dict } from '../../../utils/Dict';

type Props = {
    color?: number,
    fillColor?: number,
    outline?: number,
    dottedAlpha?: number,
    hideShading?: boolean
};

const SphereMaterial = React.forwardRef(({
  color,
  outline,
  fillColor,
  hideShading,
  ...props
}: Props, ref?: ForwardedRef<ShaderMaterial>) =>
{
  const { viewport } = useThree();
  const uniforms = useMemo(() =>
  {
    return {
      color: { value: new Color() },
      outline: { value: 0.1 },
      aspect: { value: 1.0 },
      fillColor: { value: new Color(1, 1, 1) },
      volumeColor: { value: new Color(0.92, 0.95, 0.95) },
      reflectionColor: { value: new Color(1, 1, 1) },
      lightPos: { value: new Vector3(-5, -3.5, 4.05).multiplyScalar(2.0) },
      lightBrightness: { value: 0.3 },
      lightCurve: { value: 3.0 },
      innerGlowWidth: { value: 0.04 },
      innerGlowBrightness: { value: 0.25 },
      innerGlowCurve: { value: 3.0 }
    };
  }, []);

  useEffect(() =>
  {
    uniforms.color.value.set(color || 0x000000);
  }, [ color, uniforms ]);

  useEffect(() =>
  {
    uniforms.fillColor.value.set(fillColor === undefined ? 0xffffff : fillColor);
  }, [ fillColor, uniforms ]);

  useEffect(() =>
  {
    uniforms.outline.value = outline || 0.0075;
  }, [ outline, uniforms ]);

  useEffect(() =>
  {
    uniforms.aspect.value = viewport.aspect;
  }, [ viewport, uniforms ]);

  const defines = useMemo(() =>
  {
    const def: Dict<number> = {};

    if (!hideShading) {
      def.USE_SHADING = 1;
    }

    return def;

  }, [ hideShading ]);

  return <CustomShaderMaterial
    fragmentID="jokeSphereFragmentShader"
    vertexID="jokeSphereVertexShader"
    uniforms={uniforms}
    defines={defines}
    blending={NormalBlending}
    depthWrite={false}
    side={hideShading ? BackSide : FrontSide}
    transparent
    dithering={hideShading}
    ref={ref}
    {...props}
  />;
});

SphereMaterial.displayName = 'SphereMaterial';

export default SphereMaterial;